import React from 'react'

import { useTheme, Stack, Typography, Box, Divider } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { UserBlock } from 'components'

export type BulkUserAction = 'archive' | 'delete' | 'reactivate'

interface BulkUserActionModalProps {
  users: any[]
  action: BulkUserAction
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

/**
 * Confirmation for bulk member lifecycle actions (VERA-816). Mirrors the row-level
 * Archive (soft) / Delete (hard) / Reactivate, applied to a multi-row selection.
 * The page owns the mutation loop and passes isLoading + onConfirm.
 */
export const BulkUserActionModal = (props: BulkUserActionModalProps) => {
  const { users, action, open, onClose, onConfirm, isLoading } = props
  const theme = useTheme()

  const count = users.length
  const plural = count > 1 ? 's' : ''
  const destructive = action === 'delete'

  const title =
    action === 'delete'
      ? `Delete ${count} user${plural}`
      : action === 'reactivate'
        ? `Reactivate ${count} user${plural}`
        : `Archive ${count} user${plural}`

  // No em-dash in UI copy (working-conventions §15); separate sentences instead.
  const body =
    action === 'delete'
      ? `Permanently delete the selected ${count} user${plural}? This cannot be undone. To keep their records, archive them instead.`
      : action === 'reactivate'
        ? `Reactivate the selected ${count} user${plural}? They will regain access.`
        : `Archive the selected ${count} user${plural}? They will be set inactive and lose access, but their records are kept. You can reactivate them anytime from the "Show archived" filter in the filters panel.`

  const confirmLabel = isLoading
    ? action === 'delete'
      ? 'Deleting...'
      : action === 'reactivate'
        ? 'Reactivating...'
        : 'Archiving...'
    : title

  return (
    <Modal
      title={title}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            disableElevation
            variant={destructive ? undefined : 'primary'}
            destructive={destructive}
            onClick={onConfirm}
            loading={isLoading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {body}
        </Typography>

        <Box
          sx={{
            maxHeight: 240,
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Stack divider={<Divider />}>
            {users.map((user) => (
              <Box key={user?.id} sx={{ px: 3, py: 2 }}>
                <UserBlock user={user} size={36} />
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  )
}
